import { Link, router } from "@inertiajs/react";
import { ArrowRight, ShoppingBag } from "lucide-react";
import { motion } from "motion/react";

import FlashDealBadge from "@/theme2/components/flashDeals/FlashDealBadge";
import FlashDealPrice from "@/theme2/components/flashDeals/FlashDealPrice";
import FlashDealProgress from "@/theme2/components/flashDeals/FlashDealProgress";
import { useCart } from "@/theme2/hooks/useCart";

export default function FlashDealCard({ product }) {
  const { add } = useCart();

  const handleBuyNow = () => {
    add(product, {
      quantity: 1,
      selectedColor: null,
      selectedSize: null,
    });

    router.visit("/checkout");
  };

  return (
    <motion.article
      whileHover={{ y: -6 }}
      transition={{ duration: 0.25 }}
      className="group flex h-full flex-col overflow-hidden rounded-[1.75rem] border border-border/60 bg-white p-3 shadow-xl shadow-black/5"
    >
      <div className="relative aspect-[4/5] overflow-hidden rounded-[1.4rem] bg-neutral-100">
        <Link
          href={`/products/${product.slug}`}
          aria-label={`View ${product.title}`}
          className="block size-full focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-inset"
        >
          <img
            src={product.image}
            alt={`${product.title} flash deal`}
            loading="lazy"
            className="size-full object-cover transition duration-700 group-hover:scale-105"
          />
        </Link>

        <div className="absolute left-3 top-3">
          <FlashDealBadge discount={product.discount} />
        </div>
      </div>

      <div className="flex flex-1 flex-col px-2 pb-2 pt-5">
        <p className="text-[10px] font-black uppercase tracking-[0.22em] text-muted-foreground">
          {product.series}
        </p>

        <Link
          href={`/products/${product.slug}`}
          className="mt-2 line-clamp-2 text-lg font-black tracking-tight transition hover:text-muted-foreground"
        >
          {product.title}
        </Link>

        <p className="mt-3 line-clamp-2 text-sm leading-6 text-muted-foreground">
          {product.description}
        </p>

        <div className="mt-5">
          <FlashDealPrice
            price={product.price}
            comparePrice={product.comparePrice}
          />
        </div>

        <div className="mt-5">
          <FlashDealProgress
            remainingStock={product.remainingStock}
            totalStock={product.totalStock}
          />
        </div>

        <div className="mt-auto pt-6">
          <motion.button
            type="button"
            whileTap={{ scale: 0.97 }}
            onClick={handleBuyNow}
            disabled={product.remainingStock <= 0}
            aria-label={`Buy ${product.title} now`}
            className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-full bg-black px-5 py-3 text-sm font-black uppercase text-white transition hover:bg-black focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-muted-foreground"
          >
            <ShoppingBag className="size-4" />
            Buy Now
            <ArrowRight className="size-4" />
          </motion.button>
        </div>
      </div>
    </motion.article>
  );
}